var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { UXPContext } from '@core/context/Context';
import ButtonComponent from "@widget/buttons/ButtonComponent";
import { WizardStepIndicator } from '@widget/forms/DynamicForm/WizardStepIndicator';
/**
 * A wizard-style interface to guide users through a journey.
 * You can conditionally skip steps and validate steps before proceeding.
 *
 * @example
 * ```tsx
 * <Wizard
 *   steps={[
 *     {
 *       id: "step1",
 *       title: "Personal Info",
 *       render: ({ next, prev }) => (
 *         <div>
 *           <h3>Enter your details</h3>
 *           <button onClick={() => next()}>Next</button>
 *         </div>
 *       ),
 *       onNext: () => "step2" // Optional: control navigation
 *     },
 *     {
 *       id: "step2",
 *       title: "Review",
 *       render: ({ next, prev }) => (
 *         <div>
 *           <h3>Review your information</h3>
 *           <button onClick={() => prev()}>Back</button>
 *           <button onClick={() => next()}>Complete</button>
 *         </div>
 *       )
 *     }
 *   ]}
 *   onComplete={async () => {
 *     await saveData();
 *   }}
 *   completionTitle="Finish"
 * />
 * ```
 *
 * @export
 */
const Wizard = (props) => {
    const context = React.useContext(UXPContext);
    const [stage, setStage] = React.useState('');
    const [processing, setProcessing] = React.useState(false);
    const stepHistory = React.useRef([]);
    const showStepIndicator = props.showStepIndicator !== false;
    function getCurrentStage() {
        if (!stage)
            return props.steps[0] || null;
        return props.steps.find(step => step.id === stage) || null;
    }
    function getCurrentStepIndex() {
        const currentStep = getCurrentStage();
        if (!currentStep)
            return 0;
        return props.steps.findIndex(step => step.id === currentStep.id);
    }
    function getCompletedSteps() {
        const currentIndex = getCurrentStepIndex();
        return props.steps.slice(0, currentIndex).map(step => step.id);
    }
    function renderEmpty() {
        return (_jsx("div", { className: 'uxpcore_wizard__empty', children: context.$L('uxp-core.text.no-steps-configured') }));
    }
    function renderStep(step) {
        const currentIndex = props.steps.indexOf(step);
        const isFirstStep = currentIndex === 0;
        const isLastStep = currentIndex === props.steps.length - 1;
        // Find previous step from history
        let prevStep = null;
        const history = stepHistory.current.find((h) => h.step === step.id);
        if (history) {
            prevStep = props.steps.find((s) => s.id === history.prevStep) || null;
        }
        // Determine next step
        let nextStep = props.steps[currentIndex + 1] || null;
        if (nextStep && !step.onNext)
            nextStep = null;
        function next() {
            return __awaiter(this, void 0, void 0, function* () {
                if (processing)
                    return;
                // Last step: always invoke onComplete. Validation belongs in onComplete (throw to abort).
                if (isLastStep) {
                    if (props.onComplete) {
                        setProcessing(true);
                        try {
                            yield props.onComplete();
                        }
                        catch (error) {
                            console.error('Wizard completion error:', error);
                        }
                        finally {
                            setProcessing(false);
                        }
                    }
                    return;
                }
                // Non-last steps: optional custom navigation via onNext
                if (step.onNext) {
                    const nextStepID = step.onNext();
                    if (!nextStepID) {
                        return; // Stay on current step
                    }
                    // Find the target step
                    const targetStep = props.steps.find((s) => s.id === nextStepID);
                    if (targetStep) {
                        stepHistory.current.push({ step: nextStepID, prevStep: step.id });
                        setStage(nextStepID);
                        return;
                    }
                }
                // No next step available
                if (!nextStep) {
                    console.warn(context.$L('uxp-core.text.no-next-step'));
                    return;
                }
            });
        }
        function prev() {
            if (prevStep) {
                setStage(prevStep.id);
                stepHistory.current.pop();
            }
        }
        // Convert wizard steps to WizardStepIndicator format
        const wizardSteps = props.steps.map(s => ({
            id: s.id,
            label: s.title,
            hasError: false
        }));
        return (_jsxs("div", { className: 'uxpcore_wizard__step', children: [_jsxs("div", { className: 'uxpcore_wizard__header', children: [showStepIndicator && (_jsx("div", { className: 'uxpcore_wizard__header-indicator', children: _jsx(WizardStepIndicator, { steps: wizardSteps, currentStepId: step.id, completedSteps: getCompletedSteps() }) })), _jsxs("div", { className: 'uxpcore_wizard__header-actions', children: [!isFirstStep && prevStep && (_jsx(ButtonComponent, { title: context.$L('uxp-core.text.previous'), onClick: prev, variant: 'secondary', leftIcon: 'fas chevron-left', disabled: processing })), isLastStep ? (_jsx(ButtonComponent, { title: props.completionTitle || context.$L('uxp-core.text.done'), onClick: next, variant: 'primary', rightIcon: 'fas check', loading: processing, loadingTitle: context.$L('uxp-core.text.processing') })) : (nextStep && (_jsx(ButtonComponent, { title: context.$L('uxp-core.text.next'), onClick: next, variant: 'primary', rightIcon: 'fas chevron-right', disabled: processing })))] })] }), _jsx("div", { className: 'uxpcore_wizard__content', children: step.render({ next, prev }) })] }));
    }
    const currentStep = getCurrentStage();
    return (_jsx("div", { className: 'uxpcore_wizard__container', children: !currentStep ? renderEmpty() : renderStep(currentStep) }));
};
export { Wizard };
